/**
 * OWA-691: purge service-worker cache entries that hold the SPA HTML fallback
 * under a code-asset URL.
 *
 * WHY THIS EXISTS
 * Cloudflare Pages answers a vanished /assets/* with the SPA fallback — 200
 * text/html, never a 404. Any service worker that fetches such a URL sees a
 * 200 and stores the HTML under a .js URL. From then on the browser is handed
 * HTML where it expects an ES module and throws "Failed to fetch dynamically
 * imported module". A hard reload does NOT help: it bypasses the HTTP cache,
 * not the service worker.
 *
 * Two distinct caches can hold this poison, and BOTH were observed on the
 * 2026-07-23 test reproduction:
 *   - `static-assets-cache` — the runtime CacheFirst route. Poisoned by the
 *     OLD service worker when a deploy removes the hashes it knows about.
 *   - `workbox-precache-v2-*` — the precache. Poisoned by the NEW service
 *     worker when it precaches the new manifest during Cloudflare's
 *     progressive propagation window, before every asset is live at the edge.
 *
 * The vite.config.ts `cacheWillUpdate` / `cachedResponseWillBeUsed` guards
 * cover only the first cache: workbox's `generateSW` gives no way to attach
 * plugins to the precache route. So the precache needs cleaning from outside,
 * and Cache Storage is origin-scoped rather than service-worker-scoped, which
 * means the page itself can do it.
 *
 * Deleting a poisoned entry is safe and self-healing. Both the precache route
 * and the runtime route fall back to the network on a cache miss, so the very
 * next request for that URL refetches it — by which time propagation has
 * normally completed.
 */

// Code assets are the only ones where an HTML body is unambiguously wrong. An
// HTML response under an .html or image URL may well be legitimate.
const CODE_ASSET = /\.(?:js|mjs|css|wasm)(?:\?|$)/;

export const isPoisonedEntry = (url: string, contentType: string | null): boolean =>
    CODE_ASSET.test(url) && (contentType || '').toLowerCase().startsWith('text/html');

/**
 * Scan every Cache Storage cache and delete entries that hold HTML under a
 * code-asset URL. Returns the URLs removed.
 *
 * Never throws: this runs on a recovery path where the app is already broken,
 * and a failure to clean must not mask the original error.
 */
export async function purgePoisonedAssets(): Promise<string[]> {
    if (typeof caches === 'undefined') return [];

    const purged: string[] = [];

    try {
        const names = await caches.keys();

        await Promise.all(names.map(async name => {
            try {
                const cache = await caches.open(name);
                const requests = await cache.keys();

                await Promise.all(requests.map(async request => {
                    if (!CODE_ASSET.test(request.url)) return;
                    try {
                        const cached = await cache.match(request);
                        if (!cached) return;
                        if (!isPoisonedEntry(request.url, cached.headers.get('content-type'))) return;
                        await cache.delete(request);
                        purged.push(request.url);
                    }
                    catch { /* one bad entry must not abort the sweep */ }
                }));
            }
            catch { /* one bad cache must not abort the sweep */ }
        }));
    }
    catch { /* Cache Storage unavailable (private mode, disabled) */ }

    return purged;
}
